/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useRef, useCallback } from 'react';
import { useToast } from '../context/ToastContext';
import { useAuth  } from '../context/AuthContext';
import {
  FileText, Upload, Download, Trash2, Eye,
  Search,
  Plus, X, CheckCircle,
  FolderOpen, Edit3, HardDrive
} from 'lucide-react';
import './DocumentsPage.css';

const FILE_ICONS = {
  pdf:  { icon:'📄', color:'#FF4D4D' },
  doc:  { icon:'📝', color:'#2B7CD3' },
  docx: { icon:'📝', color:'#2B7CD3' },
  xls:  { icon:'📊', color:'#217346' },
  xlsx: { icon:'📊', color:'#217346' },
  ppt:  { icon:'📋', color:'#D24726' },
  pptx: { icon:'📋', color:'#D24726' },
  txt:  { icon:'📃', color:'#888'    },
  jpg:  { icon:'🖼️', color:'#FFB830' },
  jpeg: { icon:'🖼️', color:'#FFB830' },
  png:  { icon:'🖼️', color:'#FFB830' },
  gif:  { icon:'🎞️', color:'#FF4D8D' },
  mp4:  { icon:'🎬', color:'#6C63FF' },
  mp3:  { icon:'🎵', color:'#00D68F' },
  zip:  { icon:'🗜️', color:'#888'    },
};

const getFileIcon = (name) => {
  const ext = name?.split('.').pop()?.toLowerCase();
  return FILE_ICONS[ext] || { icon:'📁', color:'#888' };
};

const formatBytes = (bytes) => {
  if (!bytes) return '0 B';
  const k=1024, sizes=['B','KB','MB','GB'];
  const i=Math.floor(Math.log(bytes)/Math.log(k));
  return `${parseFloat((bytes/Math.pow(k,i)).toFixed(1))} ${sizes[i]}`;
};

export default function DocumentsPage() {
  const { user } = useAuth();
  const toast    = useToast();

  const [files,     setFiles]     = useState([]);
  const [uploading, setUploading] = useState([]);
  const [search,    setSearch]    = useState('');
  const [filter,    setFilter]    = useState('all');
  const [preview,   setPreview]   = useState(null);
  const [dragOver,  setDragOver]  = useState(false);
  const [renaming,  setRenaming]  = useState(null);
  const [newName,   setNewName]   = useState('');

  const inputRef = useRef(null);

  // Load from localStorage
  React.useEffect(() => {
    try {
      const saved = localStorage.getItem(`nexa_docs_${user?._id||user?.id}`);
      if (saved) setFiles(JSON.parse(saved));
    } catch {}
  }, [user]);

  const saveFiles = (list) => {
    try { localStorage.setItem(`nexa_docs_${user?._id||user?.id}`, JSON.stringify(list)); } catch {}
    setFiles(list);
  };

  const handleUpload = useCallback((fileList) => {
    const accepted = Array.from(fileList);
    if (!accepted.length) return;

    accepted.forEach(file => {
      if (file.size > 50 * 1024 * 1024) { toast.error(`${file.name}: File too large (max 50MB)`); return; }

      const uploadId = `upload_${Date.now()}_${Math.random()}`;
      setUploading(prev => [...prev, { id:uploadId, name:file.name, progress:0 }]);

      // Simulate upload with FileReader
      const reader = new FileReader();
      let prog = 0;
      const interval = setInterval(() => {
        prog = Math.min(prog + Math.random()*20 + 5, 95);
        setUploading(prev => prev.map(u => u.id===uploadId ? {...u, progress:Math.round(prog)} : u));
      }, 150);

      reader.onload = (e) => {
        clearInterval(interval);
        setUploading(prev => prev.map(u => u.id===uploadId ? {...u, progress:100} : u));

        setTimeout(() => {
          const newFile = {
            id:       uploadId,
            name:     file.name,
            size:     file.size,
            type:     file.type,
            ext:      file.name.split('.').pop()?.toLowerCase(),
            dataUrl:  e.target.result,
            uploadedAt: new Date().toISOString(),
            uploadedBy: user?.displayName,
          };
          setFiles(prev => {
            const updated = [newFile, ...prev];
            saveFiles(updated);
            return updated;
          });
          setUploading(prev => prev.filter(u => u.id!==uploadId));
          toast.success(`✅ ${file.name} uploaded!`);
        }, 300);
      };

      reader.onerror = () => {
        clearInterval(interval);
        setUploading(prev => prev.filter(u => u.id!==uploadId));
        toast.error(`Failed to upload ${file.name}`);
      };

      reader.readAsDataURL(file);
    });
  }, [user, toast]);

  const handleDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    handleUpload(e.dataTransfer.files);
  };

  const handleDelete = (id) => {
    const updated = files.filter(f => f.id!==id);
    saveFiles(updated);
    if (preview?.id===id) setPreview(null);
    toast.info('File deleted');
  };

  const handleDownload = (file) => {
    const a = document.createElement('a');
    a.href = file.dataUrl;
    a.download = file.name;
    a.click();
    toast.success(`Downloading ${file.name}`);
  };

  const handleRename = (file) => {
    if (!newName.trim()) return;
    const ext = file.name.split('.').pop();
    const name = newName.includes('.') ? newName : `${newName}.${ext}`;
    const updated = files.map(f => f.id===file.id ? {...f, name} : f);
    saveFiles(updated);
    setRenaming(null); setNewName('');
    toast.success('Renamed!');
  };

  const FILTER_TABS = [
    { key:'all',       label:'All Files'  },
    { key:'documents', label:'Documents'  },
    { key:'images',    label:'Images'     },
    { key:'video',     label:'Videos'     },
    { key:'audio',     label:'Audio'      },
  ];

  const filtered = files.filter(f => {
    const matchSearch = !search || f.name.toLowerCase().includes(search.toLowerCase());
    const matchFilter = filter==='all'
      || (filter==='documents' && ['pdf','doc','docx','txt','xls','xlsx','ppt','pptx'].includes(f.ext))
      || (filter==='images'    && ['jpg','jpeg','png','gif','webp','svg'].includes(f.ext))
      || (filter==='video'     && ['mp4','mov','avi','webm'].includes(f.ext))
      || (filter==='audio'     && ['mp3','wav','ogg','m4a'].includes(f.ext));
    return matchSearch && matchFilter;
  });

  const totalSize = files.reduce((sum,f) => sum+(f.size||0), 0);

  return (
    <div className="docs-page">

      {/* Header */}
      <div className="docs-header">
        <div className="docs-header-left">
          <h1><FileText size={22} style={{color:'var(--primary-light)'}}/> Documents</h1>
          <div className="docs-stats">
            <span>{files.length} files</span>
            <span className="docs-stats-dot"/>
            <span>{formatBytes(totalSize)} used</span>
          </div>
        </div>
        <div className="docs-header-right">
          <div className="docs-search">
            <Search size={15}/>
            <input placeholder="Search files..." value={search} onChange={e=>setSearch(e.target.value)}/>
            {search && <button className="btn-icon" style={{width:24,height:24}} onClick={()=>setSearch('')}><X size={13}/></button>}
          </div>
          <button className="btn btn-primary btn-sm" onClick={() => inputRef.current?.click()}>
            <Plus size={15}/> Upload
          </button>
        </div>
      </div>

      {/* Filter tabs */}
      <div className="docs-tabs">
        {FILTER_TABS.map(t => (
          <button key={t.key} className={`docs-tab ${filter===t.key?'active':''}`} onClick={() => setFilter(t.key)}>
            {t.label}
          </button>
        ))}
      </div>

      <div className="docs-body">

        {/* Drop zone + file list */}
        <div className="docs-main">
          {/* Drop zone */}
          <div
            className={`docs-dropzone ${dragOver?'active':''}`}
            onDragOver={e=>{e.preventDefault();setDragOver(true)}}
            onDragLeave={()=>setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => inputRef.current?.click()}
          >
            <div className="docs-dropzone-icon">
              {dragOver ? <CheckCircle size={36} style={{color:'var(--green)'}}/> : <Upload size={36}/>}
            </div>
            <p className="docs-dropzone-title">{dragOver ? 'Drop to upload' : 'Drop files here or click to upload'}</p>
            <p className="docs-dropzone-sub">PDF, DOCX, TXT, Images, Videos • Max 50MB</p>
          </div>

          <input ref={inputRef} type="file" multiple style={{display:'none'}}
            accept=".pdf,.doc,.docx,.txt,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png,.gif,.mp4,.mov,.mp3,.wav,.zip"
            onChange={e => handleUpload(e.target.files)}/>

          {/* Upload progress */}
          {uploading.length > 0 && (
            <div className="docs-uploads">
              {uploading.map(u => (
                <div key={u.id} className="docs-upload-item">
                  <div className="docs-upload-info">
                    <span>{getFileIcon(u.name).icon}</span>
                    <span className="docs-upload-name">{u.name}</span>
                    <span className="docs-upload-pct">{u.progress}%</span>
                  </div>
                  <div className="docs-upload-bar">
                    <div className="docs-upload-fill" style={{width:`${u.progress}%`}}/>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Files grid */}
          {filtered.length === 0 ? (
            <div className="empty-state">
              <FolderOpen size={48}/>
              <h3>{search ? 'No files found' : 'No files yet'}</h3>
              <p>{search ? 'Try a different search' : 'Upload your first file to get started'}</p>
              {!search && (
                <button className="btn btn-primary btn-sm" onClick={() => inputRef.current?.click()}>
                  <Upload size={14}/> Upload File
                </button>
              )}
            </div>
          ) : (
            <div className="docs-grid">
              {filtered.map(file => {
                const { icon, color } = getFileIcon(file.name);
                const isImage = ['jpg','jpeg','png','gif','webp'].includes(file.ext);
                return (
                  <div key={file.id} className="doc-card">
                    {/* Thumbnail */}
                    <div className="doc-card-thumb" onClick={() => setPreview(file)}>
                      {isImage ? (
                        <img src={file.dataUrl} alt={file.name} className="doc-thumb-img"/>
                      ) : (
                        <div className="doc-thumb-icon" style={{color}}>
                          <span style={{fontSize:'2.5rem'}}>{icon}</span>
                        </div>
                      )}
                      <div className="doc-card-overlay">
                        <Eye size={20}/>
                        <span>Preview</span>
                      </div>
                    </div>

                    {/* Info */}
                    <div className="doc-card-info">
                      {renaming?.id===file.id ? (
                        <div className="doc-rename-wrap">
                          <input autoFocus value={newName}
                            onChange={e=>setNewName(e.target.value)}
                            onKeyDown={e=>{ if(e.key==='Enter') handleRename(file); if(e.key==='Escape'){setRenaming(null);setNewName('');} }}
                            className="doc-rename-input"/>
                          <button className="btn-icon" style={{width:26,height:26,color:'var(--green)'}} onClick={()=>handleRename(file)}><CheckCircle size={14}/></button>
                          <button className="btn-icon" style={{width:26,height:26}} onClick={()=>{setRenaming(null);setNewName('');}}><X size={14}/></button>
                        </div>
                      ) : (
                        <p className="doc-card-name" title={file.name}>{file.name}</p>
                      )}
                      <p className="doc-card-meta">{formatBytes(file.size)} • {new Date(file.uploadedAt).toLocaleDateString()}</p>
                    </div>

                    {/* Actions */}
                    <div className="doc-card-actions">
                      <button className="btn-icon doc-action-btn" title="Preview" onClick={() => setPreview(file)}>
                        <Eye size={15}/>
                      </button>
                      <button className="btn-icon doc-action-btn" title="Download" onClick={() => handleDownload(file)}>
                        <Download size={15}/>
                      </button>
                      <button className="btn-icon doc-action-btn" title="Rename" onClick={() => { setRenaming(file); setNewName(file.name.replace(/\.[^.]+$/,'')); }}>
                        <Edit3 size={15}/>
                      </button>
                      <button className="btn-icon doc-action-btn danger" title="Delete" onClick={() => handleDelete(file.id)}>
                        <Trash2 size={15}/>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Storage sidebar */}
        <div className="docs-sidebar">
          <div className="docs-storage-card">
            <h3><HardDrive size={16}/> Storage</h3>
            <div className="storage-visual">
              <div className="storage-circle">
                <svg viewBox="0 0 100 100" className="storage-svg">
                  <circle cx="50" cy="50" r="40" fill="none" stroke="var(--bg-500)" strokeWidth="8"/>
                  <circle cx="50" cy="50" r="40" fill="none" stroke="var(--primary)" strokeWidth="8"
                    strokeDasharray={`${Math.min((totalSize/(50*1024*1024))*251.2,251.2)} 251.2`}
                    strokeLinecap="round" transform="rotate(-90 50 50)"/>
                </svg>
                <div className="storage-circle-text">
                  <span>{Math.round((totalSize/(50*1024*1024))*100)}%</span>
                  <small>used</small>
                </div>
              </div>
            </div>
            <div className="storage-details">
              <div className="storage-row">
                <span className="storage-dot" style={{background:'var(--primary)'}}/>
                <span>Used</span>
                <span className="storage-val">{formatBytes(totalSize)}</span>
              </div>
              <div className="storage-row">
                <span className="storage-dot" style={{background:'var(--bg-400)'}}/>
                <span>Free</span>
                <span className="storage-val">{formatBytes(Math.max(0,50*1024*1024-totalSize))}</span>
              </div>
              <div className="storage-row">
                <span className="storage-dot" style={{background:'var(--border-3)'}}/>
                <span>Total</span>
                <span className="storage-val">50 MB</span>
              </div>
            </div>
          </div>

          <div className="docs-types-card">
            <h3>By Type</h3>
            {[
              { label:'Documents', ext:['pdf','doc','docx','txt','xls','xlsx'], color:'var(--blue)',   icon:'📄' },
              { label:'Images',    ext:['jpg','jpeg','png','gif','webp'],       color:'var(--yellow)', icon:'🖼️' },
              { label:'Videos',    ext:['mp4','mov','avi','webm'],              color:'var(--purple)', icon:'🎬' },
              { label:'Audio',     ext:['mp3','wav','ogg','m4a'],               color:'var(--green)',  icon:'🎵' },
            ].map(type => {
              const count = files.filter(f => type.ext.includes(f.ext)).length;
              return (
                <div key={type.label} className="docs-type-row">
                  <span>{type.icon}</span>
                  <div style={{flex:1}}>
                    <div style={{display:'flex',justifyContent:'space-between',marginBottom:4}}>
                      <span style={{fontSize:'.8rem',fontWeight:500}}>{type.label}</span>
                      <span style={{fontSize:'.75rem',color:'var(--text-400)'}}>{count}</span>
                    </div>
                    <div className="docs-type-bar">
                      <div style={{width:`${files.length?Math.min((count/files.length)*100,100):0}%`,background:type.color,height:'100%',borderRadius:'2px',transition:'width .5s ease'}}/>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Preview Modal */}
      {preview && (
        <div className="docs-preview-overlay" onClick={() => setPreview(null)}>
          <div className="docs-preview-modal" onClick={e => e.stopPropagation()}>
            <div className="docs-preview-header">
              <span className="docs-preview-name">{preview.name}</span>
              <div style={{display:'flex',gap:8}}>
                <button className="btn btn-secondary btn-sm" onClick={() => handleDownload(preview)}>
                  <Download size={14}/> Download
                </button>
                <button className="btn-icon" onClick={() => setPreview(null)}><X size={18}/></button>
              </div>
            </div>
            <div className="docs-preview-body">
              {['jpg','jpeg','png','gif','webp'].includes(preview.ext) ? (
                <img src={preview.dataUrl} alt={preview.name} style={{maxWidth:'100%',maxHeight:'70vh',objectFit:'contain',borderRadius:8}}/>
              ) : preview.ext==='pdf' ? (
                <iframe src={preview.dataUrl} title={preview.name} style={{width:'100%',height:'70vh',border:'none',borderRadius:8}}/>
              ) : preview.ext==='txt' ? (
                <div className="docs-text-preview">
                  <pre>{atob(preview.dataUrl.split(',')[1] || '')}</pre>
                </div>
              ) : preview.ext==='mp4'||preview.ext==='webm' ? (
                <video src={preview.dataUrl} controls style={{maxWidth:'100%',maxHeight:'70vh',borderRadius:8}}/>
              ) : preview.ext==='mp3'||preview.ext==='wav' ? (
                <audio src={preview.dataUrl} controls style={{width:'100%',marginTop:40}}/>
              ) : (
                <div style={{textAlign:'center',padding:'60px 20px'}}>
                  <div style={{fontSize:'4rem',marginBottom:16}}>{getFileIcon(preview.name).icon}</div>
                  <p style={{color:'var(--text-400)',marginBottom:16}}>Preview not available for this file type</p>
                  <button className="btn btn-primary btn-sm" onClick={() => handleDownload(preview)}>
                    <Download size={14}/> Download to view
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}